'use client'

import React, { FC, ReactElement } from 'react'
import useTokenOutpoints from '@/components/search-input/hooks/useTokenOutpoints'
import { Slider } from '@/components/ui/slider'
import formatNumber from '@/utils/format-number'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { NETWORK, API_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { useToast } from '@/components/ui/use-toast'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import featureFlags from '@/utils/feature-flags'
import {
	getInscriptionCollectionSlugs,
	inscriptionBelongsToCollection
} from '@/utils/inscription-collections'

import useSelect from '../../hooks/useSelect'

import Tabs from './tabs'

import Invoice from '../../components/invoice'
import { Loading } from '@/components/ui/loading'
import SelectButton from '../../components/select-button'
import { listingNoun } from './listing-noun'
import Item from './delist-item'

const _MARKET_FEE_RATE = 2.7
const _ARTIST_ROYALTY = 4.2

const CollectionDetailDelist: FC<{
	marketActions?: React.ReactNode
	tab: string
	onChangeTab: (v: string) => void
	onNavigate: (v: string) => void
	onClose: () => void
	args: any
	floorSats: string | number
	token: any
	classification: string
	collectionFilter?: string
}> = (props): ReactElement => {
	const { onClose, args, tab, onChangeTab, token, classification, collectionFilter } = props
	const { slug } = args
	const { toast } = useToast()

	//
	// Remote Data
	//

	// useExchangeRate() kept for its subscription side-effects; exchange rate unused here.
	useExchangeRate()
	const { data: runeData, isLoading } = useTokenOutpoints(token)
	const isFrozen = slug === 'frozen'

	const data = React.useMemo(() => {
		let filtered = runeData?.filter((e) => !!e.escrow)
		if (!filtered || !Array.isArray(filtered)) return filtered
		if (!isFrozen || !collectionFilter || collectionFilter === 'all') return filtered
		if (collectionFilter === 'uncategorized') {
			return filtered.filter((e) => getInscriptionCollectionSlugs(e).length === 0)
		}
		return filtered.filter((e) => inscriptionBelongsToCollection(e, collectionFilter))
	}, [runeData, collectionFilter, isFrozen])

	const ids = React.useMemo(() => {
		if (!data || !Array.isArray(data)) {
			return []
		}

		return data.map((e) => e.outpoint)
	}, [data])
	const { selected, onSelect, onSelectAll, slider, onSliderChange, allSelected } = useSelect(ids)

	// Pre-select inscription if passed via args
	React.useEffect(() => {
		if (args.inscriptionId && ids.includes(args.inscriptionId) && selected.size === 0) {
			onSelect(args.inscriptionId)
		}
	}, [args.inscriptionId, ids, onSelect, selected.size])

	//
	// Memos
	//

	const { selectedValue } = React.useMemo(() => {
		const byOutpoint = new Map((data || []).map((a: any) => [a.outpoint, a]))
		let selectedValue = 0

		// @ts-ignore
		for (let e of selected.entries()) {
			const id = e[0]
			const item = byOutpoint.get(id)
			if (item) {
				selectedValue += parseFloat(item.amount)
			}
		}

		return {
			selectedValue
		}
	}, [selected, data])

	//
	// Renders
	//

	const renderOutpoint = React.useCallback(
		(e: any) => {
			const id = e.outpoint
			return (
				<Item
					key={id}
					data={e}
					slug={slug}
					selected={selected.has(id)}
					onSelect={() => onSelect(id)}
					price={e.escrow.satoshi_price / e.amount}
				/>
			)
		},
		[onSelect, selected, slug]
	)

	const handleSubmit = React.useCallback(async () => {
		const ids = []

		// @ts-ignore
		for (const e of selected.entries()) {
			const id = e[0]
			ids.push(id)
		}

		const values = ids.map((_e) => 1e8 * 21000000)
		const psbt =
			isRune(slug) || isAlkanes(slug)
				? await Wallet.getRuneEscrows(ids, values)
				: await Wallet.getEscrows(ids, values, { forceBulk: featureFlags('app::disable_bulk') })

		if (Array.isArray(psbt)) {
			for (let i = 0; i < psbt.length; i++) {
				let id = ids[i]
				const { rawtx } = await Wallet.signEscrow(psbt[i])
				await axios.post(`${API_URL}/market/cancel-escrow`, {
					outpoint: isRune(slug) || isAlkanes(slug) ? id : undefined,
					inscription_id: !(isRune(slug) || isAlkanes(slug)) ? id : undefined,
					signature: rawtx
				})
			}
		} else {
			const { rawtx } = await Wallet.signEscrow(psbt)

			// NOTE: kept sequential to avoid hitting API rate limits when
			// delisting many items at once. /market/cancel-escrow is rate-
			// limited per source IP; parallel posts can trip 429s.
			for (let id of ids) {
				await axios.post(`${API_URL}/market/cancel-escrow`, {
					outpoint: isRune(slug) || isAlkanes(slug) ? id : undefined,
					inscription_id: !(isRune(slug) || isAlkanes(slug)) ? id : undefined,
					signature: rawtx
				})
			}

			toast({
				title: 'Success!',
				description: `You delisted ${formatNumber(selectedValue)} ${listingNoun(classification)}`
			})

			onClose()
		}
	}, [slug, selected, toast, selectedValue, classification, onClose])

	return (
		<>
			{!isFrozen && (
				<>
					<div className="flex px-2 py-1.5 w-full justify-between items-center">
						<p className="text-muted-foreground text-xs font-medium">My Wallet</p>
					</div>
					<div className="flex px-2 py-1.5 gap-4">
						<Tabs value={tab} onValueChange={onChangeTab} slug={slug} />
						<Slider
							defaultValue={[slider]}
							value={[slider]}
							max={data?.length}
							step={1}
							onValueChange={onSliderChange}
						/>
					</div>
				</>
			)}
			<div className="flex justify-between items-center px-2 py-3">
				<div className="flex gap-2 items-center">
					<SelectButton selected={allSelected} onSelect={onSelectAll} />
					<p className="text-xs text-muted-foreground select-none">
						Quantity ({selected.size > 0 ? `${selected.size}/` : ''}
						{data?.length} lots)
					</p>
				</div>
				<p className="text-xs text-muted-foreground select-none">Listing price</p>
			</div>
			{props.marketActions}
			{isLoading && (
				<div className="flex items-center justify-center min-h-[120px]">
					<Loading size={16} />
				</div>
			)}
			{data?.filter((e) => !!e.escrow).map(renderOutpoint)}
			<Invoice
				open={selected.size > 0}
				onSubmit={handleSubmit}
				action={`Delist ${formatNumber(selectedValue)} ${classification}`}
				items={[
					{ title: 'Total Fee', value: `0 ${NETWORK} / $0.00` },
					{ title: `Network Fee`, value: `0 ${NETWORK} / $0.00` }
				]}
			/>
		</>
	)
}

export default CollectionDetailDelist
